/**
 * Shipment Service
 * Handles all API calls for shipment data using Supabase
 */

import {
  FCLExportShipment,
  BOLDraft,
  DashboardData,
  DashboardStats,
  ShipmentListResponse,
  PaginationParams,
  ShipmentStatusBucket,
} from '../types/shipment.types';
import { supabase } from '../config/supabase';
import { getStoredUser } from './auth.service';

const normalizeStatus = (value?: string): string => (value || '').toLowerCase().replace(/\s+/g, '_');

const isDueConcluded = (dueStatus?: string): boolean => {
  const due = normalizeStatus(dueStatus);
  return due === 'finalizada' || due === 'averbada';
};

/**
 * DUE clearance status overrides shipment_status: a shipment whose customs
 * entry is FINALIZADA/AVERBADA is concluded regardless of its workflow stage.
 */
export function computeStatusBucket(shipmentStatus?: string, dueStatus?: string): ShipmentStatusBucket {
  if (isDueConcluded(dueStatus)) return 'concluded';
  const status = normalizeStatus(shipmentStatus);
  if (status === 'completed' || status === 'finished') return 'concluded';
  if (status === 'submitted') return 'submitted';
  if (status === 'in_progress') return 'in_progress';
  return 'draft';
}

/**
 * Port names cache to avoid repeated database queries
 */
const portNamesCache: Map<string, string> = new Map();

/**
 * Get port name from port code (locode)
 */
async function getPortName(portCode: string): Promise<string> {
  if (!portCode) return '';
  
  // Check cache first
  if (portNamesCache.has(portCode)) {
    return portNamesCache.get(portCode)!;
  }
  
  try {
    const { data, error } = await supabase
      .from('ports')
      .select('name')
      .eq('locode', portCode)
      .single();
    
    if (error || !data) {
      // If port not found, return the code itself
      portNamesCache.set(portCode, portCode);
      return portCode;
    }
    
    // Cache the result
    portNamesCache.set(portCode, data.name);
    return data.name;
  } catch (err) {
    // On error, return the code
    return portCode;
  }
}

export async function getPortNameByLocode(portCode: string): Promise<string> {
  return getPortName(portCode);
}

export async function getCustomersByIds(ids: (number | string)[]): Promise<Record<string | number, { name: string; awb_bl_info: string }>> {
  try {
    const { data, error } = await supabase
      .from('customers')
      .select('id, customer_name, awb_bl_info')
      .in('id', ids);

    console.log('[getCustomersByIds] ids:', ids, '| data:', data, '| error:', error);

    if (error || !data) return {};
    return Object.fromEntries(
      data.map((c: any) => [c.id, { name: c.customer_name || '', awb_bl_info: c.awb_bl_info || '' }])
    );
  } catch (err) {
    console.error('[getCustomersByIds] exception:', err);
    return {};
  }
}

export async function getCustomerDetails(customerId: number | string) {
  try {
    const user = getStoredUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase
      .from('customers')
      .select('id, customer_name, customer_address, awb_bl_info')
      .eq('id', customerId)
      .single();

    console.log(`[getCustomerDetails] id=${customerId}`, { data, error });

    if (error || !data) {
      console.warn('[getCustomerDetails] query failed:', error?.message, error?.code);
      return {
        id: customerId,
        name: '',
        awb_bl_info: '',
      };
    }

    return {
      id: data.id,
      name: data.customer_name || '',
      awb_bl_info: data.awb_bl_info || data.customer_address || '',
    };
  } catch (error) {
    console.error('[getCustomerDetails] exception:', error);
    return {
      id: customerId,
      name: '',
      awb_bl_info: '',
    };
  }
}

/**
 * Get dashboard statistics and summary data
 */
export async function getDashboardData(): Promise<DashboardData> {
  try {
    const user = getStoredUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const brokerId = user.broker.id;
    console.log('Fetching dashboard data for broker:', brokerId, typeof brokerId);

    // Get all shipments for this broker - brokerId can be UUID or number
    const { data: shipments, error } = await supabase
      .from('fcl_export_shipments')
      .select('*')
      .eq('customs_broker_id', brokerId);

    if (error) {
      console.error('Error fetching dashboard data:', error);
      throw new Error(`Failed to fetch shipments: ${error.message}`);
    }

    console.log('Fetched shipments:', shipments?.length || 0);

    const dueMap: Map<any, string> = new Map();
    try {
      if (shipments && shipments.length > 0) {
        const shipmentIds = shipments.map((s: any) => s.id);
        const { data: dueRows, error: dueError } = await supabase
          .from('customs_due')
          .select('shipment_id,due_status')
          .in('shipment_id', shipmentIds);

        if (!dueError && dueRows) {
          dueRows.forEach((r: any) => dueMap.set(r.shipment_id, r.due_status));
        }
      }
    } catch (e) {
      console.warn('Unable to load customs_due table for dashboard stats', e);
    }

    const stats: DashboardStats = {
      total_shipments: shipments?.length || 0,
      draft_count: 0,
      in_progress_count: 0,
      submitted_count: 0,
      completed_count: 0,
    };
    shipments?.forEach((s: any) => {
      const bucket = computeStatusBucket(s.shipment_status, dueMap.get(s.id) || s.due_status);
      if (bucket === 'draft') stats.draft_count++;
      else if (bucket === 'in_progress') stats.in_progress_count++;
      else if (bucket === 'submitted') stats.submitted_count++;
      else stats.completed_count++;
    });

    return {
      broker_name: user.broker.name,
      stats,
      recent_shipments: [], // Will be populated by getShipments
    };
  } catch (error: any) {
    console.error('Error in getDashboardData:', error);
    throw new Error(error.message || 'Failed to fetch dashboard data');
  }
}

/**
 * Get paginated list of shipments with filters
 */
export async function getShipments(
  filters?: PaginationParams
): Promise<ShipmentListResponse> {
  try {
    const user = getStoredUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const brokerId = user.broker.id;
    const page = filters?.page || 1;
    const perPage = filters?.limit || 10;
    const offset = (page - 1) * perPage;

    console.log('[Shipments] Fetching for broker:', brokerId, 'Type:', typeof brokerId);

    // Build query - join with customers table to get customer_name
    // Using left join syntax with foreign key constraint name
    let query = supabase
      .from('fcl_export_shipments')
      .select(`
        *,
        shipper:customers!fcl_export_shipments_shipper_id_fkey(customer_name)
      `, { count: 'exact' })
      .eq('customs_broker_id', brokerId);

    // Apply search filter - use actual column names: brl_reference, pol, pod, vessel
    if (filters?.search) {
      const searchTerm = `%${filters.search}%`;
      query = query.or(`brl_reference.ilike.${searchTerm},consignee_name.ilike.${searchTerm},pol.ilike.${searchTerm},pod.ilike.${searchTerm},vessel.ilike.${searchTerm},booking.ilike.${searchTerm}`);
    }

    // Apply sorting - use actual DB column names
    const sortField = filters?.sort_by || 'created_at';
    const sortOrder = filters?.sort_order || 'desc';
    query = query.order(sortField as any, { ascending: sortOrder === 'asc' });

    // status_bucket depends on customs_due (joined below), which Supabase can't
    // filter server-side in this query, so pagination happens after the bucket
    // is computed client-side instead of via .range() up front.
    const statusBucket = filters?.status_bucket;
    if (!statusBucket) {
      query = query.range(offset, offset + perPage - 1);
    }

    const { data: shipments, error, count } = await query;

    if (error) {
      console.error('Error fetching shipments:', error);
      throw error;
    }
    // Attempt to load DUE number + status from `customs_due` table in a single query
    const dueMap: Map<any, { due?: string; due_status?: string }> = new Map();
    try {
      if (shipments && shipments.length > 0) {
        const shipmentIds = shipments.map((s: any) => s.id);
        const { data: dueRows, error: dueError } = await supabase
          .from('customs_due')
          .select('shipment_id,due,due_status')
          .in('shipment_id', shipmentIds);

        if (!dueError && dueRows) {
          dueRows.forEach((r: any) => dueMap.set(r.shipment_id, { due: r.due, due_status: r.due_status }));
        }
      }
    } catch (e) {
      // If the table doesn't exist or query fails, continue gracefully
      console.warn('Unable to load customs_due table, falling back to shipment.due_status', e);
    }

    let pageRows = shipments || [];
    let total = count || pageRows.length;

    if (statusBucket) {
      const matched = pageRows.filter(
        (s: any) => computeStatusBucket(s.shipment_status, dueMap.get(s.id)?.due_status || s.due_status) === statusBucket
      );
      total = matched.length;
      pageRows = matched.slice(offset, offset + perPage);
    }

    // Map to ShipmentListItem format - using actual DB column names
    // Database uses: brl_reference, pol, pod, etd, shipment_status
    const shipmentList = pageRows?.map((s: any) => ({
      id: s.id,
      shipment_number: s.brl_reference || s.booking || `SHP-${s.id}`,
      shipper_name: s.shipper?.customer_name || s.shipper_name || '',
      shipper_order: s.shipper_order || '',
      booking: s.booking || '',
      vessel: s.vessel || '',
      deadline_draft: s.deadline_draft || '',
      deadline_draft_time: s.deadline_draft_time || '',
      // Prefer DUE number/status from customs_due table when available
      due_number: dueMap.get(s.id)?.due || s.due || '',
      due_status: dueMap.get(s.id)?.due_status || s.due_status || '',
      draft_submitted: s.draft_submitted || null,
      draft_approved: s.draft_approved || null,
      draft_date: s.draft_date || null,
      origin_port: s.pol || s.port_of_loading || '',
      destination_port: s.pod || s.port_of_discharge || '',
      expected_sailing_date: s.etd || '',
      status: s.shipment_status || 'draft',
      consignee_name: s.consignee_name || '',
      has_draft: false, // TODO: join with bol_drafts table
      created_at: s.created_at,
    })) || [];

    return {
      success: true,
      shipments: shipmentList,
      pagination: {
        page,
        limit: perPage,
        total,
        total_pages: Math.ceil(total / perPage),
      },
    };
  } catch (error: any) {
    console.error('Error in getShipments:', error);
    return {
      success: false,
      shipments: [],
      pagination: { page: 1, limit: 10, total: 0, total_pages: 0 },
      error: error.message || 'Failed to fetch shipments',
    };
  }
}

/**
 * Get detailed shipment information by ID
 */
export async function getShipmentDetail(shipmentId: string | number): Promise<FCLExportShipment> {
  try {
    const user = getStoredUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const brokerId = user.broker.id;

    // First try with joins, but if that fails, get shipment without joins
    let shipment;
    let error;
    
    // Try to fetch with customer joins
    const result = await supabase
      .from('fcl_export_shipments')
      .select(`
        *,
        shipper:customers!fcl_export_shipments_shipper_id_fkey(customer_name, customer_address, awb_bl_info),
        consignee:customers!fcl_export_shipments_consignee_id_fkey(customer_name, customer_address, awb_bl_info)
      `)
      .eq('id', shipmentId)
      .eq('customs_broker_id', brokerId)
      .single();
    
    shipment = result.data;
    error = result.error;
    
    // If join failed, try without joins
    if (error) {
      console.warn('Join query failed, fetching without joins:', error);
      const simpleResult = await supabase
        .from('fcl_export_shipments')
        .select('*')
        .eq('id', shipmentId)
        .eq('customs_broker_id', brokerId)
        .single();
      
      shipment = simpleResult.data;
      error = simpleResult.error;
    }

    if (error) {
      console.error('Error fetching shipment detail:', error);
      throw error;
    }

    if (!shipment) {
      throw new Error('Shipment not found');
    }

    console.log('Raw shipment data from database:', shipment);

    // Map database fields to expected interface
    const mappedShipment = {
      ...shipment,
      broker_name: user.broker.name,
      // Override with joined customer data if available
      shipper_name: shipment.shipper?.customer_name || shipment.shipper_name || '',
      shipper_address: shipment.shipper?.customer_address || shipment.shipper_address || '',
      shipper_awb_bl_info: shipment.shipper?.awb_bl_info || '',
      consignee_name: shipment.consignee?.customer_name || shipment.consignee_name || '',
      consignee_address: shipment.consignee?.customer_address || shipment.consignee_address || '',
      consignee_awb_bl_info: shipment.consignee?.awb_bl_info || '',
      // Normalize field names and database-specific columns
      shipment_number: shipment.shipment_number || shipment.brl_reference || shipment.booking || `SHP-${shipment.id}`,
      marks_and_numbers: shipment.marks_and_numbers || shipment.container_details || shipment.shipment_number || '',
      ncm_hs: shipment.ncm_hs || shipment.ncm_hs_code || '',
      package_description: shipment.package_description || shipment.cargo_description || '',
      delivery_instructions: shipment.delivery_instructions || shipment.special_instructions || '',
      origin_port: shipment.origin_port || shipment.pol || shipment.port_of_loading || '',
      destination_port: shipment.destination_port || shipment.pod || shipment.port_of_discharge || '',
      expected_sailing_date: shipment.expected_sailing_date || shipment.etd || '',
      place_of_loading: shipment.port_of_loading || '',
      port_of_discharge: shipment.port_of_discharge || '',
      place_of_receipt: shipment.place_of_receipt || '',
      place_of_delivery: shipment.place_of_delivery || '',
      status: shipment.status || shipment.shipment_status || 'draft',
    };

    console.log('Mapped shipment data:', mappedShipment);
    return mappedShipment;
  } catch (error: any) {
    console.error('Error in getShipmentDetail:', error);
    throw new Error(error.message || 'Failed to fetch shipment details');
  }
}

/**
 * Save or update BOL draft
 * Falls back to storing in fcl_export_shipments if bol_drafts table doesn't exist
 */
export async function saveBOLDraft(
  shipmentId: string | number,
  draftData: any,
  markAsSubmitted: boolean = false
): Promise<{ success: boolean; message: string; draft?: BOLDraft }> {
  try {
    const user = getStoredUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const brokerId = user.broker.id;

    // Verify shipment belongs to this broker
    const { data: shipment, error: shipmentError } = await supabase
      .from('fcl_export_shipments')
      .select('id, customs_broker_id')
      .eq('id', shipmentId)
      .eq('customs_broker_id', brokerId)
      .single();

    if (shipmentError || !shipment) {
      throw new Error('Shipment not found or access denied');
    }

    // Try to save to bol_drafts table first
    try {
      // Check if draft already exists
      const { data: existingDraft } = await supabase
        .from('bol_drafts')
        .select('*')
        .eq('shipment_id', shipmentId)
        .single();

      let result;

      if (existingDraft) {
        // Update existing draft
        const { data, error } = await supabase
          .from('bol_drafts')
          .update({
            draft_data: draftData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingDraft.id)
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        // Create new draft - user_id is optional now
        const { data, error } = await supabase
          .from('bol_drafts')
          .insert({
            shipment_id: shipmentId,
            user_id: user.id || null,
            draft_data: draftData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (error) throw error;
        result = data;
      }

      // If marking as submitted, update the shipment record
      if (markAsSubmitted) {
        const { error: updateError } = await supabase
          .from('fcl_export_shipments')
          .update({
            draft_submitted: true,
            draft_date: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', shipmentId);

        if (updateError) {
          console.error('Error updating draft_submitted flag:', updateError);
          // Don't fail the entire save if this update fails
        }
      }

      return {
        success: true,
        message: markAsSubmitted ? 'BOL draft submitted successfully' : 'BOL draft saved successfully',
        draft: result,
      };
    } catch (draftError: any) {
      // If bol_drafts table doesn't exist, save BOL data to fcl_export_shipments
      console.warn('bol_drafts table not available, saving to fcl_export_shipments:', draftError);
      
      // Update shipment with BOL data
      const { error: updateError } = await supabase
        .from('fcl_export_shipments')
        .update({
          // Shipper info
          shipper_name: draftData.shipper_name,
          shipper_address: draftData.shipper_address,
          
          // Consignee info
          consignee_name: draftData.consignee_name,
          consignee_address: draftData.consignee_address,
          
          // Notify party
          notify_party_name: draftData.notify_party_name,
          notify_party_address: draftData.notify_party_address,
          
          // Transport details
          pre_carriage_by: draftData.pre_carriage_by,
          place_of_receipt: draftData.place_of_receipt,
          vessel: draftData.vessel,
          voyage: draftData.voyage,
          port_of_loading: draftData.port_of_loading,
          port_of_discharge: draftData.port_of_discharge,
          place_of_delivery: draftData.place_of_delivery,
          
          // Cargo details
          marks_and_numbers: draftData.marks_and_numbers,
          ncm_hs: draftData.ncm_hs,
          package_description: draftData.package_description,
          gross_weight: draftData.gross_weight,
          measurement: draftData.measurement,
          delivery_instructions: draftData.delivery_instructions,
          
          // Freight details
          freight_details: draftData.freight_details,
          prepaid: draftData.prepaid,
          collect: draftData.collect,
          freight_payable_at: draftData.freight_payable_at,
          
          // BL details
          place_date_of_issue: draftData.place_date_of_issue,
          number_of_original_bl: draftData.number_of_original_bl,
          bl_signature: draftData.bl_signature,
          
          // Status fields
          draft_submitted: markAsSubmitted ? true : undefined,
          draft_date: markAsSubmitted ? new Date().toISOString() : undefined,
          updated_at: new Date().toISOString(),
        })
        .eq('id', shipmentId);

      if (updateError) throw updateError;

      return {
        success: true,
        message: markAsSubmitted ? 'BOL draft submitted successfully' : 'BOL draft saved successfully',
      };
    }
  } catch (error: any) {
    console.error('Error saving BOL draft:', error);
    return {
      success: false,
      message: error.message || 'Failed to save BOL draft',
    };
  }
}

/**
 * Get BOL draft for a shipment
 * Falls back to reading from fcl_export_shipments if bol_drafts table doesn't exist
 */
export async function getBOLDraft(shipmentId: string | number): Promise<BOLDraft | null> {
  try {
    const user = getStoredUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const brokerId = user.broker.id;

    // Verify shipment belongs to this broker and get its data
    const { data: shipment } = await supabase
      .from('fcl_export_shipments')
      .select('*')
      .eq('id', shipmentId)
      .eq('customs_broker_id', brokerId)
      .single();

    if (!shipment) {
      throw new Error('Shipment not found or access denied');
    }

    // Try to fetch from bol_drafts table
    try {
      const { data: draft, error } = await supabase
        .from('bol_drafts')
        .select('*')
        .eq('shipment_id', shipmentId)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned, which is fine
        // Any other error means table might not exist
        throw error;
      }

      if (draft) {
        return draft;
      }
    } catch (draftError: any) {
      console.warn('Could not fetch from bol_drafts table (table may not exist):', draftError.message);
    }

    // Fallback: Check if shipment has BOL data and return it as a draft
    if (shipment.shipper_name || shipment.consignee_name || shipment.vessel) {
      console.log('Using BOL data from fcl_export_shipments table');
      return {
        id: 0,
        shipment_id: shipmentId as number,
        user_id: user.id || 0,
        draft_data: {
          shipper_name: shipment.shipper_name || '',
          shipper_address: shipment.shipper_address || '',
          consignee_name: shipment.consignee_name || '',
          consignee_address: shipment.consignee_address || '',
          notify_party_name: shipment.notify_party_name || '',
          notify_party_address: shipment.notify_party_address || '',
          pre_carriage_by: shipment.pre_carriage_by || '',
          place_of_receipt: shipment.place_of_receipt || '',
          delivery_instructions: shipment.delivery_instructions || shipment.special_instructions || '',
          vessel: shipment.vessel || '',
          voyage: shipment.voyage || '',
          port_of_loading: shipment.port_of_loading || '',
          port_of_discharge: shipment.port_of_discharge || '',
          place_of_delivery: shipment.place_of_delivery || '',
          marks_and_numbers: shipment.marks_and_numbers || shipment.container_details || shipment.shipment_number || '',
          ncm_hs: shipment.ncm_hs || shipment.ncm_hs_code || '',
          package_description: shipment.package_description || shipment.cargo_description || '',
          gross_weight: shipment.gross_weight || '',
          measurement: shipment.measurement || '',
          container_details: [],
          freight_details: shipment.freight_details || '',
          prepaid: shipment.prepaid || false,
          collect: shipment.collect || false,
          freight_payable_at: shipment.freight_payable_at || '',
          place_date_of_issue: shipment.place_date_of_issue || '',
          number_of_original_bl: shipment.number_of_original_bl || '3',
          signature: shipment.bl_signature || '',
          bl_signature: shipment.bl_signature || '',
        },
        status: 'draft',
        created_at: shipment.created_at,
        updated_at: shipment.updated_at,
      } as BOLDraft;
    }

    return null;
  } catch (error: any) {
    console.error('Error fetching BOL draft:', error);
    return null;
  }
}

/**
 * Get containers for a shipment from containers_exp table
 */
export async function getShipmentContainers(shipmentId: string | number): Promise<any[]> {
  try {
    const user = getStoredUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const brokerId = user.broker.id;

    // Verify shipment belongs to this broker
    const { data: shipment } = await supabase
      .from('fcl_export_shipments')
      .select('id')
      .eq('id', shipmentId)
      .eq('customs_broker_id', brokerId)
      .single();

    if (!shipment) {
      throw new Error('Shipment not found or access denied');
    }

    // Fetch containers from containers_exp table
    const { data: containers, error } = await supabase
      .from('containers_exp')
      .select('*')
      .eq('shipment_id', shipmentId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching containers:', error);
      throw error;
    }

    return containers || [];
  } catch (error: any) {
    console.error('Error in getShipmentContainers:', error);
    return [];
  }
}

/**
 * Save or update container details in containers_exp table
 */
export async function saveContainerDetails(
  shipmentId: string | number,
  containers: Array<{
    container: string;
    type: string;
    tare: string;
    seal_number: string;
    gross_weight?: string;
    net_weight?: string;
    cbm?: string;
    qty?: string;
    vgm?: string;
  }>
): Promise<{ success: boolean; message: string }> {
  try {
    const user = getStoredUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    const brokerId = user.broker.id;

    // Verify shipment belongs to this broker
    const { data: shipment } = await supabase
      .from('fcl_export_shipments')
      .select('id')
      .eq('id', shipmentId)
      .eq('customs_broker_id', brokerId)
      .single();

    if (!shipment) {
      throw new Error('Shipment not found or access denied');
    }

    // Fetch existing containers
    const { data: existingContainers } = await supabase
      .from('containers_exp')
      .select('id, container_number')
      .eq('shipment_id', shipmentId);

    const existingMap = new Map(
      existingContainers?.map((c: any) => [c.container_number, c.id]) || []
    );

    // Process each container
    const promises = containers.map((container) => {
      const existingId = existingMap.get(container.container);

      if (existingId) {
        // Update existing container
        return supabase
          .from('containers_exp')
          .update({
            container_type: container.type,
            seal: container.seal_number,
            tare: container.tare,
            gross_weight: container.gross_weight ?? null,
            net_weight:   container.net_weight   ?? null,
            cbm:          container.cbm          ?? null,
            qty:          container.qty          ?? null,
            vgm:          container.vgm          ?? null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingId);
      } else {
        // Insert new container
        return supabase
          .from('containers_exp')
          .insert({
            shipment_id: shipmentId,
            container_number: container.container,
            container_type: container.type,
            seal: container.seal_number,
            tare: container.tare,
            gross_weight: container.gross_weight ?? null,
            net_weight:   container.net_weight   ?? null,
            cbm:          container.cbm          ?? null,
            qty:          container.qty          ?? null,
            vgm:          container.vgm          ?? null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
      }
    });

    const results = await Promise.all(promises);

    // Check for errors
    const errors = results.filter((r) => r.error);
    if (errors.length > 0) {
      console.error('Errors saving containers:', errors);
      throw new Error('Failed to save some containers');
    }

    return {
      success: true,
      message: 'Container details saved successfully',
    };
  } catch (error: any) {
    console.error('Error saving container details:', error);
    return {
      success: false,
      message: error.message || 'Failed to save container details',
    };
  }
}
